import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Link2 } from 'lucide-react';
import { isLscBillingUiEnabled } from '../../../config/featureFlags';
import {
  bindLscPaidSubscriptionToLeague,
  getLscBillingStatus,
  LscApiError,
} from '../../api';
import { formatBindLeagueError } from '../../entitlementBillingErrors';
import { LSC_SUBSCRIPTION_PRODUCT_NAME } from '../../billingCopy';
import type { SetupWizardStep } from '../../leagueSetupWizard';
import {
  buildLeaguePresentationInput,
  resolveLscBillingPresentation,
} from '../../billingPresentation';
import { lscPath } from '../../routing';
import BillingDisabledBanner from '../BillingDisabledBanner';
import BillingStatusCard from '../BillingStatusCard';
import BillingSupportContact from '../../billingSupportContact';
import LeagueCheckoutActions from '../LeagueCheckoutActions';
import LeagueSetupStepCard from '../LeagueSetupStepCard';
import LoadingState from '../LoadingState';

const bindButtonClass =
  'btn-primary inline-flex items-center justify-center gap-2 min-h-11 px-4 w-full sm:w-auto';

export interface SubscriptionStepPanelProps {
  leagueId: string;
  wizardStep: SetupWizardStep;
  isOwner: boolean;
  isOperator: boolean;
  leagueArchived?: boolean;
  onSetupRefresh?: () => void;
}

const SubscriptionStepPanel: React.FC<SubscriptionStepPanelProps> = ({
  leagueId,
  wizardStep,
  isOwner,
  isOperator,
  leagueArchived = false,
  onSetupRefresh,
}) => {
  const billingUiEnabled = isLscBillingUiEnabled();
  const [billingStatus, setBillingStatus] = useState<Awaited<
    ReturnType<typeof getLscBillingStatus>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bindLoading, setBindLoading] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  const [bindSuccess, setBindSuccess] = useState(false);

  const presentation = useMemo(
    () =>
      resolveLscBillingPresentation(
        buildLeaguePresentationInput(billingStatus, 'setup_subscription', {
          isOwner,
          isOperator,
        }, {
          billingUiEnabled,
          leagueId,
          leagueArchived,
        })
      ),
    [billingStatus, billingUiEnabled, isOperator, isOwner, leagueArchived, leagueId]
  );

  const loadStatus = useCallback(async () => {
    if (isOperator && !isOwner) return;
    setLoading(true);
    setLoadError(null);
    try {
      const status = await getLscBillingStatus(leagueId);
      setBillingStatus(status);
    } catch (err) {
      if (err instanceof LscApiError && (err.status === 401 || err.status === 403)) {
        setBillingStatus(null);
        setLoadError(null);
      } else {
        setLoadError('Billing information is unavailable right now.');
      }
    } finally {
      setLoading(false);
    }
  }, [isOperator, isOwner, leagueId]);

  useEffect(() => {
    if (isOperator && !isOwner) {
      setLoading(false);
      return;
    }
    void loadStatus();
  }, [isOperator, isOwner, loadStatus]);

  const handleBind = async () => {
    setBindLoading(true);
    setBindError(null);
    setBindSuccess(false);
    try {
      const result = await bindLscPaidSubscriptionToLeague(leagueId);
      if (result.success) {
        setBindSuccess(true);
        await loadStatus();
        onSetupRefresh?.();
        return;
      }
      setBindError('Could not bind subscription to this league. Please try again or contact support.');
    } catch (err) {
      setBindError(formatBindLeagueError(err));
    } finally {
      setBindLoading(false);
    }
  };

  if (isOperator && !isOwner) {
    const ownerManagedPresentation = resolveLscBillingPresentation(
      buildLeaguePresentationInput(null, 'setup_subscription', {
        isOwner: false,
        isOperator: true,
      }, {
        billingUiEnabled,
        leagueId,
        leagueArchived,
      })
    );
    return (
      <div className="space-y-4 overflow-x-hidden" data-testid="subscription-step-panel">
        <LeagueSetupStepCard step={wizardStep} />
        <div
          className="card space-y-2"
          data-testid="subscription-step-owner-managed"
          role="status"
        >
          {ownerManagedPresentation.detailLines.map((line) => (
            <p key={line} className="text-sm text-secondary-300">
              {line}
            </p>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="subscription-step-panel-loading">
        <LoadingState message="Loading league subscription…" />
      </div>
    );
  }

  return (
    <div className="space-y-4 overflow-x-hidden" data-testid="subscription-step-panel">
      <LeagueSetupStepCard step={wizardStep} />

      <div className="card space-y-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-white">{LSC_SUBSCRIPTION_PRODUCT_NAME}</h3>
          <p className="text-sm text-secondary-400">{presentation.valueProposition}</p>
          {presentation.detailLines.map((line) => (
            <p key={line} className="text-xs text-secondary-500">
              {line}
            </p>
          ))}
        </div>

        {presentation.disabledBanner === 'ui_disabled' && (
          <BillingDisabledBanner variant="ui" message={presentation.disabledReason} />
        )}

        {loadError && (
          <p className="text-sm text-red-400" role="alert" data-testid="subscription-step-load-error">
            {loadError}
          </p>
        )}

        {presentation.showStatusCard && (
          <div data-testid="subscription-step-read-only-bound">
            <BillingStatusCard
              status={billingStatus}
              loading={false}
              error={null}
              onRetry={() => void loadStatus()}
              returnLeagueId={leagueId}
            />
          </div>
        )}

        {presentation.showBindCta && (
          <div
            className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 space-y-3"
            data-testid="subscription-step-paid-unbound"
            role="status"
          >
            <button
              type="button"
              className={bindButtonClass}
              onClick={() => void handleBind()}
              disabled={bindLoading || leagueArchived}
              data-testid="subscription-step-bind-cta"
            >
              <Link2 className="h-4 w-4 shrink-0" aria-hidden />
              {bindLoading
                ? 'Binding subscription…'
                : presentation.primaryCta?.label || 'Bind subscription to this league'}
            </button>
            {bindError && (
              <p className="text-sm text-red-300" role="alert" data-testid="subscription-step-bind-error">
                {bindError}
              </p>
            )}
            {bindSuccess && (
              <p
                className="text-sm text-green-300"
                role="status"
                data-testid="subscription-step-bind-success"
              >
                Subscription bound to this league.
              </p>
            )}
          </div>
        )}

        {presentation.showCheckoutActions && (
          <div className="space-y-3" data-testid="subscription-step-checkout-section">
            <LeagueCheckoutActions leagueId={leagueId} />
          </div>
        )}

        {presentation.disabledBanner === 'lifecycle' &&
          !presentation.showCheckoutActions &&
          !presentation.showBindCta && (
            <BillingDisabledBanner variant="checkout" message={presentation.disabledReason} />
          )}

        <BillingSupportContact />
        <Link
          to={lscPath(`/leagues/${leagueId}/settings`)}
          className="text-sm text-primary-300 hover:text-primary-200 inline-flex min-h-11 items-center"
          data-testid="subscription-step-settings-link"
        >
          Open league subscription settings
        </Link>
      </div>
    </div>
  );
};

export default SubscriptionStepPanel;
